/**
 * Permission model and resolver — story 0.4, PROJECT-OVERVIEW §5.3, BR-18.
 *
 * 🔒 **CLIENT-SIDE GATING IS UX, NEVER A SECURITY BOUNDARY (BR-17).**
 * A hidden button is a courtesy — it stops a user walking into a refusal they
 * could not have predicted. **The server refusing the call is the control.**
 * Nothing in this module protects data, and nothing here should ever be described
 * as if it did.
 *
 * 🔒 **Effective permission is the UNION of the user's grupos.** A user in grupo A
 * (`ver` on Contratos) and grupo B (`editar` on Contratos) ends up with
 * `{ver: true, editar: true}`. Computing this as an intersection is the single most
 * likely bug in the story, so `ver` and `editar` are OR-ed **independently** and
 * tested as a matrix.
 *
 * There is **no runtime profile picker** and there must never be one: Eduardo
 * judged the union *"mais fluida para o usuário"*, and a picker would let a user
 * appear to drop a permission they still hold.
 */

/** The backend's model — `PERSONAS-ref.md` §6. Ours maps onto it, not the reverse. */
export type Nivel = 'nenhum' | 'leitura' | 'escrita'

/** What a screen actually asks: may I show this, and may I offer to change it. */
export interface Acesso {
  ver: boolean
  editar: boolean
}

export interface Permissao {
  /** A tela/aba key from `app/navigation.ts` — `finops.oportunidades`, `admin.perfis`. */
  idFuncionalidade: string
  nivel: Nivel
}

export interface Grupo {
  idGrupo: string
  nome: string
  permissoes: Permissao[]
}

/** `Map<telaOuAba, {ver, editar}>` — story 0.4 AC-2. */
export type MapaPermissoes = ReadonlyMap<string, Acesso>

export const SEM_ACESSO: Acesso = { ver: false, editar: false }

/**
 * AC-6 — the mapping to the backend's model. **No backend change is implied.**
 * `escrita` implies `leitura`: there is no "may edit but may not see".
 */
export function deNivel(nivel: Nivel): Acesso {
  switch (nivel) {
    case 'escrita':
      return { ver: true, editar: true }
    case 'leitura':
      return { ver: true, editar: false }
    case 'nenhum':
      return SEM_ACESSO
  }
}

/**
 * The inverse, for the `2q` screen (EPIC-004) which edits grupos and must send the
 * backend its own vocabulary back.
 *
 * `{ver: false, editar: true}` is not representable and cannot arise from `deNivel`
 * — it collapses to `nenhum` rather than silently becoming `escrita`, because
 * granting write off the back of an impossible state is the dangerous direction.
 */
export function paraNivel(acesso: Acesso): Nivel {
  if (!acesso.ver) return 'nenhum'
  return acesso.editar ? 'escrita' : 'leitura'
}

/**
 * The union — AC-1, AC-2.
 *
 * Resolved ONCE per session by `PermissionProvider`, never per render: it is a fold
 * over every permission of every grupo, and a screen that recomputed it on each
 * render would do that work thousands of times for an answer that cannot change.
 */
export function resolverPermissoes(grupos: readonly Grupo[]): MapaPermissoes {
  const mapa = new Map<string, Acesso>()

  for (const grupo of grupos) {
    for (const { idFuncionalidade, nivel } of grupo.permissoes) {
      const novo = deNivel(nivel)
      const atual = mapa.get(idFuncionalidade) ?? SEM_ACESSO
      // OR, per field and independently. `Math.max`-style truthiness on the pair as
      // a whole would let a `leitura` grant overwrite an `escrita` one depending on
      // grupo order — order must not matter, and this is what makes it not matter.
      mapa.set(idFuncionalidade, {
        ver: atual.ver || novo.ver,
        editar: atual.editar || novo.editar,
      })
    }
  }

  return mapa
}

/** Lookup with the safe default: an unknown key grants nothing. */
export function acessoDe(mapa: MapaPermissoes, chave: string): Acesso {
  return mapa.get(chave) ?? SEM_ACESSO
}
